// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { openSnackbar } from './snackbar';
import { getUsers } from 'api/user';
import { fetchClientSettings } from './settings';

// ----------------------------------------------------------------------

const initialState = {
    total: {
        all: 0,
        items: 0
    },
    list: [],
    loading: false,
    pagination: {
        pageNumber: 0,
        pageSize: 10
    },
    filter: {
        showInactive: false,
        showAssessorsOnly: false
    }
};

const slice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUsers(state, action) {
            state.list = action.payload?.data || [];
            state.total.all = action.payload?.pagination?.total || 0;
            state.total.items = action.payload?.pagination?.totalItems || 0;
        },
        setPagination(state, action) {
            state.pagination = action.payload;
        },
        setFilter(state, action) {
            state.filter = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function fetchUsers(params) {
    return async (dispatch, getState) => {
        const { pagination, filter } = getState().user;

        dispatch(slice.actions.setLoading(true));

        try {
            //Merge  params with current pagination
            const query = {
                pagination: params?.pagination || pagination,
                search: params?.search || '',
                ...(params?.filter ? params.filter : filter)
            };
            const result = await getUsers(query);

            dispatch(slice.actions.setUsers(result));

            if (params?.pagination) {
                dispatch(slice.actions.setPagination(params.pagination));
            }
            if (params?.filter) {
                dispatch(slice.actions.setFilter(params.filter));
            }
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createUser(data, refreshParams) {
    return async (dispatch) => {
        const response = await axios.post('/api/user', data);

        // Fetch updated list of users
        dispatch(fetchUsers(refreshParams));
        dispatch(fetchClientSettings())
        return response.data;
    };
}

export function editUser(userId, data, refreshParams) {
    return async (dispatch) => {
        const response = await axios.patch(`/api/user/${userId}`, data);

        // Fetch updated list of users
        dispatch(fetchUsers(refreshParams));
        dispatch(fetchClientSettings())

        return response.data;
    };
}

export function resetPassword(userId) {
    return async (dispatch) => {
        await axios.patch(`/api/user/reset-password/${userId}`);
        dispatch(fetchUsers());
    };
}

export function requestPasswordReset(email, hasCaptcha = false, type = '', assessmentCode = '') {
    return async (dispatch) => {
        dispatch(
            openSnackbar({
                open: true,
                message: 'Sending password reset request...',
                variant: 'progress'
            })
        );
        try {
            const response = await axios.post('/api/auth/request-reset-password', { email, hasCaptcha, type, assessmentCode });
            const result = response.data;
            dispatch(
                openSnackbar({
                    open: true,
                    title: 'Password Reset Request Succeeded',
                    message: 'Follow the email instructions to reset your password',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
            return result;
        } catch (err) {
            console.log('err', err);
            const { code = null } = err;
            if (code) {
                const result = err;
                // check what type of code
                if (result.code === 'too.many.request') {
                    dispatch(
                        openSnackbar({
                            open: true,
                            title: 'Password Reset Failed.',
                            message: result.message,
                            variant: 'alert',
                            alert: {
                                color: 'error'
                            }
                        })
                    );
                } else if (result.code === 'show-captcha') {
                    dispatch(
                        openSnackbar({
                            open: false
                        })
                    );
                }
                return result;
            } else {
                dispatch(
                    openSnackbar({
                        open: true,
                        title: 'Password Reset Failed.',
                        message: err?.message || 'Please check your internet connection.',
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        }
                    })
                );
            }
        }
    };
}
